import { useEffect, useState } from 'react';
import { USUARIO_STATUS, normalizarStatusUsuario } from '../constants/status';
import { mapCanonicalRole, type UserRole } from '../lib/authRoles';
import { isInvalidRefreshTokenError, supabase } from '../lib/supabase';

export type { UserRole };

interface AuthUser {
  id: string;
  email: string;
  nome: string;
  role: UserRole;
  status: string;
  empresa_id?: string | null;
}

interface UseAuthReturn {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  hasRole: (allowedRoles: UserRole[]) => boolean;
}

let cachedUser: AuthUser | null = null;
let cachedAuthId: string | null = null;

export function resetAuthCache() {
  cachedUser = null;
  cachedAuthId = null;
}

function mapRole(role?: string | null, tipoUsuario?: string | null): UserRole {
  return mapCanonicalRole(role, tipoUsuario);
}

async function fetchCanonicalUser(authUserId: string, authEmail = ''): Promise<AuthUser> {
  const { data, error } = await supabase
    .from('usuarios')
    .select('id, email, nome, role, tipo_usuario, status, empresa_id')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (error) throw error;
  if (!data) throw new Error('PERFIL_CANONICO_NAO_ENCONTRADO');

  const status = (data.status ?? '').toLowerCase().trim();
  const normalizedStatus = normalizarStatusUsuario(status);
  if (
    normalizedStatus === USUARIO_STATUS.SUSPENDED
    || ['pending', 'pendente', 'pendente_analise', 'em_analise'].includes(status)
  ) {
    throw new Error('PERFIL_CANONICO_INATIVO');
  }

  return {
    id: data.id,
    email: data.email ?? authEmail,
    nome: data.nome ?? 'Usuário',
    role: mapRole(data.role, data.tipo_usuario),
    status,
    empresa_id: data.empresa_id ?? null,
  };
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(cachedUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolveSession = async () => {
      setLoading(true);
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!session?.user) {
          resetAuthCache();
          if (!cancelled) setUser(null);
          return;
        }

        if (cachedUser && cachedAuthId === session.user.id) {
          if (!cancelled) setUser(cachedUser);
          return;
        }

        const resolved = await fetchCanonicalUser(session.user.id, session.user.email ?? '');
        cachedUser = resolved;
        cachedAuthId = session.user.id;
        if (!cancelled) setUser(resolved);
      } catch (error) {
        if (isInvalidRefreshTokenError(error) || (error as Error)?.message === 'PERFIL_CANONICO_INATIVO') {
          try { await supabase.auth.signOut(); } catch { /* best effort */ }
        }
        resetAuthCache();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void resolveSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session?.user) {
        resetAuthCache();
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        resetAuthCache();
        void resolveSession();
      }
    });

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        resetAuthCache();
        void resolveSession();
      }
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    hasRole: (roles) => !!user && roles.includes(user.role),
  };
}
